import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import './PopoverWrapper.scss';

type TPopoverPosition = 'top' | 'bottom';
type TPopoverTrigger = 'click' | 'hover';

interface IPopoverProps {
  Target: React.ComponentType;
  Content: React.ComponentType;
  position?: TPopoverPosition;
  trigger?: TPopoverTrigger;
  offset?: number;
}

export const Popover = ({
  Target,
  Content,
  position = 'top',
  trigger = 'click',
  offset = 10,
}: IPopoverProps) => {
  const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const togglePopover = () => {
    if (trigger === 'hover') return;
    setIsPopoverVisible((prev) => !prev);
  };

  const handleMouseEnter = () => {
    if (trigger !== 'hover') return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsPopoverVisible(true);
  };

  const handleMouseLeave = () => {
    if (trigger !== 'hover') return;
    hideTimeoutRef.current = setTimeout(() => {
      setIsPopoverVisible(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (trigger !== 'click') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !parentRef.current?.contains(event.target as Node)
      ) {
        setIsPopoverVisible(false);
      }
    };

    if (isPopoverVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverVisible, trigger]);

  const getPopoverStyles = () => {
    if (position === 'bottom') {
      return {
        top: `calc(100% + ${offset}px)`,
        bottom: 'auto',
        transform: 'translateX(-50%)',
      };
    }
    if (position === 'top') {
      return {
        bottom: `calc(100% + ${offset}px)`,
        top: 'auto',
        transform: 'translateX(-50%)',
      };
    }

    return {};
  };

  return (
    <div
      className="popover-container"
      ref={parentRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button type="button" className="popover-target" onClick={togglePopover}>
        <Target />
      </button>

      <motion.div
        initial="hide"
        animate={isPopoverVisible ? 'show' : 'hide'}
        transition={{ duration: 0.25 }}
        variants={variants}
        className={`popover-box ${position}`}
        style={getPopoverStyles()}
        ref={popoverRef}
      >
        <div className="popover-content">
          <Content />
        </div>
      </motion.div>
    </div>
  );
};

const variants = {
  show: { opacity: 1, display: 'flex' },
  hide: { opacity: 0, transitionEnd: { display: 'none' } },
};
